import { useState } from "react";
import "../../style/scss/form.scss";
import { Form, FormInput, FormActions } from "../Controls/Form";
import API from "@src/API.js";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { SMS_CONSENT_BODY } from "../../marketing/smsConsentCopy";

const emptyRegister = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  passwordConfirm: "",
  phone: "",
  smsConsent: false,
};

function validationMessage(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  return payload.error || payload.message || payload.msg || fallback;
}

export default function RegisterForm({
  embedded = false,
  onClose,
  onSwitchToLogin,
}) {
  const [registerData, updateRegisterData] = useState(emptyRegister);
  const [busy, setBusy] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const navigate = useNavigate();

  async function validateField(route, body) {
    const resp = await API.postAsync(route, body);
    if (!resp || !resp.ok) {
      throw new Error(validationMessage(resp?.jsonData, `Validation failed (${route})`));
    }
    const data = resp.jsonData;
    if (data && data.isValid === false) {
      throw new Error(validationMessage(data, `Validation failed (${route})`));
    }
    return data;
  }

  async function onDoneAsync(event) {
    event.preventDefault();
    if (busy) return;
    setAttemptedSubmit(true);
    if (!isRequiredFilled() || !isPwValid() || !isPhoneValid() || !hasPhoneConsent()) {
      return;
    }
    setBusy(true);
    try {
      await validateField("/RegisterValidateUserName", {
        firstName: registerData.firstName,
        lastName: registerData.lastName,
      });
      await validateField("/RegisterValidateEmail", { email: registerData.email });
      await validateField("/RegisterValidatePassword", {
        password: registerData.password,
        passwordConfirm: registerData.passwordConfirm,
      });
      if ((registerData.phone || "").trim()) {
        await validateField("/RegisterValidatePhone", { phone: registerData.phone });
      }

      const resp = await API.postAsync("/RegisterPost", {
        firstName: registerData.firstName,
        lastName: registerData.lastName,
        email: registerData.email,
        password: registerData.password,
        confirmPassword: registerData.passwordConfirm,
        phone: registerData.phone,
        smsConsent: registerData.smsConsent,
      });

      if (resp && resp.ok) {
        window.sessionStorage.setItem(
          "PendingRegistrationCodes",
          JSON.stringify({ smsSent: resp.jsonData?.smsSent === true })
        );
        toast.success("Registration started. Check your email for the verification link.");
        onClose?.();
        navigate("/register-code");
      } else {
        toast.error(validationMessage(resp?.jsonData, "Registration failed"));
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Registration failed");
    } finally {
      setBusy(false);
    }
  }

  function isRequiredFilled() {
    return !!(
      (registerData.firstName || "").trim() &&
      (registerData.lastName || "").trim() &&
      (registerData.email || "").trim() &&
      (registerData.password || "").length > 0
    );
  }

  function isPwValid() {
    const password = registerData.password || "";
    const confirm = registerData.passwordConfirm || "";
    if (!password || !confirm) return false;
    if (password !== confirm) return false;
    if (password.length < 6) return false;
    // Keep in sync with CareerMasterAPI Validations.AllowedPasswordChars
    const allowed = /^[a-zA-Z0-9!@#$%^&*_-]+$/;
    return allowed.test(password);
  }

  function passwordInvalidMessage() {
    const password = registerData.password || "";
    const confirm = registerData.passwordConfirm || "";
    if (!password || !confirm) return "Password and confirmation are required";
    if (password.length < 6) return "Password must be at least 6 characters";
    if (!/^[a-zA-Z0-9!@#$%^&*_-]+$/.test(password)) {
      return "Passwords may contain only letters, numbers, or !@#$%^&*_-";
    }
    if (password !== confirm) return "Passwords do not match";
    return "Invalid password";
  }

  function isPhoneValid() {
    const digits = (registerData.phone || "").replace(/[^0-9]/g, "");
    return !digits || digits.length >= 10;
  }

  function hasPhoneConsent() {
    return !(registerData.phone || "").trim() || registerData.smsConsent === true;
  }

  function isFilled(field) {
    return () => !!(registerData[field] || "").toString().trim();
  }

  function goToLogin(e) {
    e?.preventDefault?.();
    if (onSwitchToLogin) {
      onSwitchToLogin();
      return;
    }
    navigate("/");
  }

  const afterAttempt = attemptedSubmit;

  return (
    <div className={embedded ? "loginPage loginPage--embedded" : "loginPage"}>
      <Form
        className={embedded ? "login loginForm" : "centered login loginForm"}
        label="Register"
        object={registerData}
        onUpdate={updateRegisterData}
      >
        <FormInput
          label="First Name"
          field="firstName"
          isValid={afterAttempt ? isFilled("firstName") : undefined}
          invalidMessage="Required"
        />
        <FormInput
          label="Last Name"
          field="lastName"
          isValid={afterAttempt ? isFilled("lastName") : undefined}
          invalidMessage="Required"
        />
        <FormInput
          label="Email"
          field="email"
          type="email"
          isValid={afterAttempt ? isFilled("email") : undefined}
          invalidMessage="Required"
        />
        <FormInput
          label="Password"
          field="password"
          type="password"
          isValid={afterAttempt ? isFilled("password") : undefined}
          invalidMessage="Required"
        />
        <FormInput
          label="Password Confirm"
          field="passwordConfirm"
          type="password"
          isValid={afterAttempt ? isPwValid : undefined}
          invalidMessage={passwordInvalidMessage()}
        />
        <FormInput
          label="Phone (optional)"
          field="phone"
          type="phone"
          isValid={afterAttempt ? isPhoneValid : undefined}
          invalidMessage="Invalid phone number"
        />
        {(registerData.phone || "").trim() ? (
          <label className="mkt-checkbox">
            <input
              type="checkbox"
              checked={registerData.smsConsent === true}
              onChange={(event) =>
                updateRegisterData({ ...registerData, smsConsent: event.target.checked })
              }
            />
            <span>
              {SMS_CONSENT_BODY}{" "}
              <Link to="/privacy">Privacy Policy</Link> and <Link to="/terms">Terms</Link>.
            </span>
            {afterAttempt && !hasPhoneConsent() ? (
              <small className="form-input-error">SMS consent is required when adding a phone number.</small>
            ) : null}
          </label>
        ) : null}
        <FormActions
          doneLabel={busy ? "Registering…" : "Register"}
          onSaveAsync={async (e) => {
            await onDoneAsync(e);
          }}
        >
          {embedded ? (
            <p className="mkt-auth-switch">
              Already have an account?{" "}
              {onSwitchToLogin ? (
                <button type="button" className="mkt-text-link" onClick={goToLogin}>
                  Log in
                </button>
              ) : (
                <Link to="/" onClick={goToLogin}>
                  Login
                </Link>
              )}
            </p>
          ) : null}
        </FormActions>
      </Form>
      {!embedded ? (
        <p className="centered">
          Already have an account? <Link to="/">Login</Link>
        </p>
      ) : null}
    </div>
  );
}
